import type { EditorJsData } from './editorjs-types';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import './editor.css';
import MakeChangeTune from './editorjs-make-change-tune';
import { MakeChangeModal } from './prompt-command';
import { SubpagesBlock } from './subpages-block.tsx';

export interface EditorJsEditorHandle {
  getValue: () => EditorJsData | null;
  resetContent: (value: EditorJsData | null) => void;
}

interface EditDocumentEditorProps {
  /** Initial content as Editor.js data. */
  value?: EditorJsData | null;
  /** Document id for AI processing prompts (undefined for new/unsaved docs). */
  documentId?: string;
  /** Whether this is a new document (not yet saved). */
  isNewDocument: boolean;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Current document path for context-aware tools like Subpages. */
  parentPath: string;
  /** Current child documents for the Subpages block. */
  children: Array<{ id: string; path: string; label?: string }>;
  /** Open a React-powered prompt to create a subpage. */
  onCreateSubpage?: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
}

export const EditDocumentEditor = forwardRef<
  EditorJsEditorHandle,
  EditDocumentEditorProps
>(function EditDocumentEditor(
  {
    value,
    documentId,
    isNewDocument,
    onUpload,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
  },
  ref,
) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any | null>(null);
  const latestValueRef = useRef<EditorJsData | null>(value ?? null);

  const [makeChangeOpen, setMakeChangeOpen] = useState(false);
  const [makeChangeBlockId, setMakeChangeBlockId] = useState<string | null>(
    null,
  );

  const requestMakeChange = useCallback((blockId: string) => {
    setMakeChangeBlockId(blockId || null);
    setMakeChangeOpen(true);
  }, []);

  const handleMakeChangeOpenChange = useCallback((open: boolean) => {
    setMakeChangeOpen(open);
    if (!open) setMakeChangeBlockId(null);
  }, []);

  const [portalTargets, setPortalTargets] = useState<Map<string, HTMLElement>>(
    () => new Map(),
  );

  const registerPortalTarget = useCallback((id: string, el: HTMLElement) => {
    setPortalTargets((prev) => {
      const next = new Map(prev);
      next.set(id, el);
      return next;
    });
  }, []);

  const unregisterPortalTarget = useCallback((id: string) => {
    setPortalTargets((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const subpagesPortalNodes = useMemo(() => {
    return Array.from(portalTargets.entries()).map(([id, el]) =>
      createPortal(
        <SubpagesBlock
          key={id}
          parentPath={parentPath}
          children={children}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
        />,
        el,
      ),
    );
  }, [
    portalTargets,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
  ]);

  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      if (!holderRef.current) return;

      try {
        const [
          { default: EditorJS },
          { default: CodeTool },
          HeaderModule,
          { default: ImageTool },
          { default: ListTool },
          { SubpagesTool },
          { default: TableWithImagesTool },
          { default: MermaidTool },
        ] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/code'),
          import('@editorjs/header'),
          import('@editorjs/image'),
          import('@editorjs/list'),
          import('./editorjs-subpages-tool'),
          import('./editorjs-table-with-images'),
          import('./editorjs-mermaid-tool'),
        ]);

        if (cancelled || !holderRef.current) return;

        const editor = new EditorJS({
          holder: holderRef.current,
          data: (latestValueRef.current as any) ?? { blocks: [] },
          autofocus: true,
          tools: {
            header:
              typeof HeaderModule === 'function'
                ? HeaderModule
                : {
                    // Fallback tool config for headers
                    class: (HeaderModule as any).default ?? HeaderModule,
                    config: {
                      levels: [1, 2, 3],
                      defaultLevel: 2,
                    },
                  },
            list: ListTool,
            code: CodeTool,
            image: {
              class: ImageTool,
              config: onUpload
                ? {
                    uploader: {
                      async uploadByFile(file: File) {
                        const url = await onUpload(file);
                        return {
                          success: 1,
                          file: {
                            url,
                          },
                        };
                      },
                    },
                  }
                : undefined,
            },
            table: {
              class: TableWithImagesTool,
              config: {
                insertImageButtonText: 'Insert image',
                uploader: onUpload
                  ? {
                      async uploadByFile(file: File) {
                        const url = await onUpload(file);
                        return {
                          success: 1,
                          file: {
                            url,
                          },
                        };
                      },
                    }
                  : undefined,
              },
            },
            mermaid: MermaidTool,
            subpages: {
              class: SubpagesTool,
              config: {
                registerPortalTarget,
                unregisterPortalTarget,
              },
            },
            makeChangeTune: {
              class: MakeChangeTune,
              config: {
                onMakeChangeRequested: requestMakeChange,
              },
            },
          },
          tunes: ['makeChangeTune'],
          onReady: () => {
            // editorjs-mermaid is a wrapper around mermaid; configure theme once.
            try {
              if (typeof MermaidTool?.config === 'function') {
                MermaidTool.config({ theme: 'neutral' });
              }
            } catch {
              // Ignore plugin config failures (diagram rendering still attempts on demand).
            }
          },
          async onChange(api) {
            const data = (await api.saver.save()) as EditorJsData;
            latestValueRef.current = data;
          },
        });

        editorRef.current = editor;
      } catch {
        if (holderRef.current) {
          holderRef.current.innerText =
            'Failed to load editor. Check console for details.';
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      const editor = editorRef.current;
      if (editor) {
        void editor.isReady
          .then(() => {
            editor.destroy();
            editorRef.current = null;
          })
          .catch(() => {
            editorRef.current = null;
          });
      }
      setPortalTargets(new Map());
    };
  }, [
    onUpload,
    registerPortalTarget,
    unregisterPortalTarget,
    requestMakeChange,
  ]);

  // When the `value` prop changes (e.g. new document loaded), re-render content
  useEffect(() => {
    if (!editorRef.current) return;
    latestValueRef.current = value ?? { blocks: [] };
    void editorRef.current.isReady
      .then(() =>
        editorRef.current.render(
          (latestValueRef.current as any) ?? { blocks: [] },
        ),
      )
      .catch(() => {});
  }, [value]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => latestValueRef.current,
      resetContent: (data: EditorJsData | null) => {
        latestValueRef.current = data ?? { blocks: [] };
        const editor = editorRef.current;
        if (!editor) return;
        void editor.isReady
          .then(() =>
            editor.render((latestValueRef.current as any) ?? { blocks: [] }),
          )
          .catch(() => {});
      },
    }),
    [],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 'calc(100% - 96px)',
        minHeight: '300px',
        padding: '0 48px',
      }}
    >
      {subpagesPortalNodes}
      <div
        style={{
          flex: 1,
          minHeight: '300px',
          width: '100%',
        }}
      >
        <div
          ref={holderRef}
          style={{
            height: '100%',
            width: '100%',
            minHeight: '300px',
          }}
          className="editorjs-root editorjs-holder"
        />
      </div>
      <MakeChangeModal
        open={makeChangeOpen}
        onOpenChange={handleMakeChangeOpenChange}
        documentId={documentId}
        isNewDocument={isNewDocument}
        focusBlockId={makeChangeBlockId}
      />
    </div>
  );
});
